import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Tabs,
  Tab,
  Divider,
  Snackbar,
  Alert,
  AlertColor,
  Button,
  styled,
} from '@mui/material';
import BackLink from '../../BackLink';
import TabPanel from '../../TabPanel';
import EntityTitle from '../../EntityTitle';
import EmojiTransportationIcon from '@mui/icons-material/EmojiTransportation';
import ContactInfo from './ContactInfo';
import BillingInfo from './BillingInfo';
import LegalInfo from './LegalInfo';
import TransportationInfo from './TransportationInfo';
import * as providerSlice from '../../../data/provider.slice';
import * as vehicleSlice from '../../../data/vehicle.slice';
import ProviderDialog from './ProviderDialog';
import Drivers from '../drivers/Drivers';
import Vehicles from '../vehicles/Vehicles';
import Users from '../users/Users';
import InsuranceInfo from './InsuranceInfo';
import ProviderDocuments from './ProviderDocuments';
import Notes from './Notes';
import Entity, { entityClasses } from '../Entity';
import { useLoggedInProviderId } from '../../../hooks/useLoggedInProviderId';
import { useCanViewNotes } from '../../../hooks/permissions/useCanViewNotes';
import { useCanEditProvider } from '../../../hooks/permissions/useCanEditProvider';
import TripPreferences from './TripPreferences/TripPreferences';
import ProviderServicePricing from './ProviderServicePricing';
import { Provider as ProviderModel } from '../../../data/models/provider.model';
import { useIsPRAdmin } from '../../../hooks/permissions/useIsPRAdmin';
import { useIsPRUser } from '../../../hooks/permissions/useIsPRUser';

const PREFIX = 'Provider';

const classes = {
  header: `${PREFIX}-header`,
};

const Header = styled(Box)(({ theme }) => ({
  [`&.${classes.header}`]: {
    display: 'flex',
    padding: theme.spacing(1),
    justifyContent: 'space-between',
  },
}));

export default function Provider() {
  const { t } = useTranslation('common');
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const canEditProvider = useCanEditProvider(parseInt(params.id!, 10));
  const providerId = useLoggedInProviderId();
  const canViewNotes = useCanViewNotes();
  const isPRUser = useIsPRUser();
  const isPRAdmin = useIsPRAdmin();

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [logoUrl, setLogoUrl] = React.useState<string>();
  const [redirecting, setRedirecting] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState<string>();
  const [snackbarSeverity, setSnackbarSeverity] =
    React.useState<AlertColor>('error');

  const loading = useAppSelector(providerSlice.selectLoadingProvider);
  const loadingError = useAppSelector(providerSlice.selectLoadingProviderError);
  const provider = useAppSelector(
    providerSlice.selectProvider,
  ) as ProviderModel;
  const uploadingDocument = useAppSelector(
    providerSlice.selectUploadingDocument,
  );
  const tabOneIndex = useAppSelector(providerSlice.selectTabsetOneIndex);
  const tabTwoIndex = useAppSelector(providerSlice.selectTabsetTwoIndex);
  const savingSuccess = useAppSelector(providerSlice.selectSavingSuccess);
  const uploadError = useAppSelector(providerSlice.selectUploadError);

  React.useEffect(() => {
    if (uploadError) {
      setSnackbarMessage(t(uploadError));
      setSnackbarSeverity('error');
    }
  }, [uploadError]);

  React.useEffect(() => {
    const id = parseInt(params.id!, 10);
    // This is temporary to make sure user doesn't load another providers info.
    // This really needs to be done in the API
    if (typeof providerId === 'number' && id !== providerId) {
      const idx = window.location.href.lastIndexOf('/');
      const url = window.location.href.substring(0, idx + 1) + providerId;
      window.location.replace(url);
      setRedirecting(true);
    } else {
      if (provider?.transportationProviderID === id) {
        return;
      }
      dispatch(providerSlice.getProviderAsync(id));
      dispatch(
        vehicleSlice.setVehicleList({
          itemCount: 0,
          currentPage: 1,
          totalPages: 0,
          items: [],
        }),
      );
    }
  }, [params.id, providerId, dispatch]);

  React.useEffect(() => {
    if (providerId) {
      const settings = provider?.settings || [];
      const setting = settings.find(
        (s: any) => s.SettingOption.ProviderSettingOptionBC === 'LOGOURL',
      );
      if (setting) {
        setLogoUrl(setting.settingValue);
      }
    }
  }, [providerId, provider]);

  React.useEffect(() => {
    const id = parseInt(params.id || '0', 10);
    if (provider?.transportationProviderID === id) {
      if (provider?.transportationProviderTierID === 3) {
        navigate(`/providers/tier3/${id}`);
      }
    }
  }, [params.id, provider]);

  React.useEffect(() => {
    if (providerId) {
      setSnackbarMessage(savingSuccess || undefined);
      setSnackbarSeverity('success');
    }
  }, [savingSuccess, providerId]);

  React.useEffect(() => {
    setSnackbarMessage(loadingError || undefined);
    setSnackbarSeverity('error');
  }, [loadingError]);

  const handleTopTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    dispatch(providerSlice.setTabsetOneIndex(newValue));
  };

  const handleBottomTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: number,
  ) => {
    dispatch(providerSlice.setTabsetTwoIndex(newValue));
  };

  const editProvider = () => {
    setEditorOpen(true);
  };

  const closeProviderEditor = () => {
    setEditorOpen(false);
    dispatch(providerSlice.resetSavingFlags());
  };

  const onSnackbarClose = () => {
    setSnackbarMessage(undefined);
    dispatch(providerSlice.setSavingSuccess(null));
    dispatch(providerSlice.setUploadError(null));
  };

  const navigateToProviderHome = () => {
    navigate(`/providerview/${provider?.transportationProviderID}`);
  };

  // This is temporary until we handle the provider security on the server.
  if (redirecting) {
    return null;
  }

  return (
    <Entity>
      <Header className={classes.header}>
        {!providerId && <BackLink />}
        {(isPRUser || isPRAdmin) && (
          <Button
            variant="contained"
            color="primary"
            onClick={navigateToProviderHome}>
            {t('app.rideCafe')}
          </Button>
        )}
      </Header>
      <Box component="div" className={entityClasses.content}>
        <Box component="div" className={entityClasses.left}>
          <Card className={entityClasses.card}>
            <CardHeader
              title={
                <EntityTitle
                  icon={<EmojiTransportationIcon fontSize="large" />}
                  label={provider?.transportationProviderName}
                  loading={loading}
                  readOnly={!canEditProvider}
                  logo={logoUrl}
                  onEdit={editProvider}
                />
              }
            />
            <Divider />
            {!loading && (
              <CardContent>
                <ContactInfo
                  provider={provider}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            )}
          </Card>

          {!loading && (
            <Card className={entityClasses.card}>
              <CardContent>
                <BillingInfo
                  provider={provider}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            </Card>
          )}

          {!loading && (
            <Card className={entityClasses.card}>
              <CardContent>
                <LegalInfo
                  provider={provider}
                  titleStyle={entityClasses.section}
                />
              </CardContent>
            </Card>
          )}
        </Box>
        {!loading && (
          <Box component="div" className={entityClasses.right}>
            <Box component="div">
              <Paper className={entityClasses.tabs}>
                <Tabs
                  indicatorColor="primary"
                  textColor="primary"
                  value={tabOneIndex}
                  onChange={handleTopTabChange}
                  variant="fullWidth">
                  <Tab label={t('provider.info')} />
                  <Tab label={t('common.documents')} />
                  <Tab label={t('common.insurance')} />
                  <Tab label={t('preferences.title')} />
                </Tabs>
                <TabPanel value={tabOneIndex} index={0}>
                  <TransportationInfo
                    provider={provider}
                    isInternalUser={!providerId}
                  />
                </TabPanel>
                <TabPanel value={tabOneIndex} index={1}>
                  <ProviderDocuments
                    provider={provider}
                    uploadingDocument={uploadingDocument}
                    readOnly={!canEditProvider}
                    allowView={!providerId}
                  />
                </TabPanel>
                <TabPanel value={tabOneIndex} index={2}>
                  <InsuranceInfo provider={provider} />
                </TabPanel>
                <TabPanel value={tabOneIndex} index={3}>
                  <TripPreferences
                    providerId={provider?.transportationProviderID}
                  />
                </TabPanel>
              </Paper>
            </Box>

            <Box component="div">
              <Paper className={entityClasses.tabs}>
                <Tabs
                  indicatorColor="primary"
                  textColor="primary"
                  value={tabTwoIndex}
                  onChange={handleBottomTabChange}
                  variant="fullWidth">
                  <Tab label={t('common.drivers')} />
                  <Tab label={t('common.vehicles')} />
                  <Tab label={t('common.users')} />
                  <Tab label={t('provider.servicePricing')} />
                  {canViewNotes ? <Tab label={t('provider.notes')} /> : null}
                </Tabs>
                <TabPanel value={tabTwoIndex} index={0}>
                  {provider ? <Drivers provider={provider} /> : <></>}
                </TabPanel>
                <TabPanel value={tabTwoIndex} index={1}>
                  <Vehicles provider={provider} />
                </TabPanel>
                <TabPanel value={tabTwoIndex} index={2}>
                  <Users provider={provider} />
                </TabPanel>
                <TabPanel value={tabTwoIndex} index={3}>
                  {provider ? (
                    <ProviderServicePricing provider={provider} />
                  ) : null}
                </TabPanel>
                {canViewNotes ? (
                  <TabPanel value={tabTwoIndex} index={4}>
                    <Notes providerId={parseInt(params.id!, 10)} />
                  </TabPanel>
                ) : null}
              </Paper>
            </Box>
          </Box>
        )}
      </Box>
      {provider ? (
        <ProviderDialog
          title={t('providers.edit')}
          open={editorOpen}
          provider={provider}
          close={closeProviderEditor}
        />
      ) : null}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert variant="filled" severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Entity>
  );
}
